import React, { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Waypoint } from 'react-waypoint';

import { FontAwesomeIcon } from '@magoosh/lib/icons';

import { SchoolProfile } from '@schools/app/types';
import ImageSlider from '@schools/app/components/image_slider';
import SectionNav from '@schools/app/components/section_nav';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  offsetY: number;
  profile: SchoolProfile;
  sectionNavHeight: number;
  stickyHeroBarHeight: number;
}

const ProfileHeroAndSectionNav: React.FC<Props> = (props) => {
  const waypointRef = useRef(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const isLoaded = !!props.profile;

  // If page is reloaded and the user is past the waypoint it won't trigger.
  // Do a one time check if we are already past the waypoint and show the sticky bar if so.
  useLayoutEffect(() => {
    const rect = waypointRef?.current?.getBoundingClientRect();
    if (!rect) return;

    if (rect.y < 0) setIsSticky(true);
  }, []);

  return (
    <>
      <div
        className={cx(style.hero, { [style.sticky]: isSticky })}
        style={{
          position: 'relative'
        }}
      >
        <div className={cx(style.content, 'd-flex')}>
          <div className={cx(style.contentText, 'd-flex flex-column flex-1')} style={{ padding: 15 }}>
            {
              isLoaded && (
                <>
                  <div>
                    <img
                      alt={`${props.profile.name} Logo`}
                      className={style.logoMain}
                      src={`${props.profile.logo}?fit=fill&h=192&w=192`}
                      style={{ borderRadius: 8 }}
                    />
                  </div>

                  <div>
                    <h1 className="u-margin-A-n" style={{ color: colors.grayDarker }}>
                      <strong>{props.profile.name}</strong>
                    </h1>
                  </div>

                  <div className="d-flex flex-column" style={{ gap: 10 }}>
                    {
                      (props.profile.programAttributes || []).map((attribute, idx) => (
                        <div key={idx} className="align-items-center d-flex" style={{ gap: 10 }}>
                          <FontAwesomeIcon icon={attribute.icon} style={{ textAlign: 'center', width: 25 }} />

                          <div>{attribute.value}</div>
                        </div>
                      ))
                    }
                  </div>
                </>
              )
            }
          </div>

          <div className={style.contentImages}>
            <ImageSlider aspectRatio={3/2}>
              {
                (props.profile?.coverPhotos || []).map((asset, idx) => (
                  <div key={idx}>
                    <img
                      alt={asset.title}
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      src={`${asset.url}?fit=fill&h=390&w=585`}
                      style={{ height: 'auto', width: '100%' }}
                    />
                  </div>
                ))
              }
            </ImageSlider>
          </div>
        </div>

        {/*Trigger the waypoint a bit above what would be the top of the sticky element*/}
        <div ref={waypointRef} style={{ position: 'absolute', bottom: props.stickyHeroBarHeight * 2 }}>
          <Waypoint
            onEnter={() => setIsSticky(false)}
            onLeave={() => setIsSticky(true)}
          />
        </div>
      </div>

      {/*Sticky bar*/}
      <div
        className="hidden-xs"
        style={{
          backgroundColor: '#fff',
          height: isSticky ? props.stickyHeroBarHeight : 0,
          position: 'sticky',
          top: props.offsetY,
          width: '100%',
          // Below style.categoryProfileListOverlay which has $zindex-navbar-fixed
          zIndex: 1000
        }}
      >
        <div
          style={{
            backgroundColor: colors.grayLightest,
            height: '100%',
            opacity: isSticky ? 1 : 0,
            transition: isSticky ? 'opacity 1s ease-out' : 'none'
          }}
        >
          <div className="container" style={{ height: '100%' }}>
            <div className="align-items-center d-flex" style={{ gap: 20, height: '100%' }}>
              {
                isLoaded && (
                  <>
                    <div>
                      <img
                        alt={`${props.profile.name} Logo`}
                        className={style.logoSticky}
                        src={`${props.profile.logo}?fit=fill&h=192&w=192`}
                        style={{ borderRadius: 4 }}
                      />
                    </div>

                    <div>
                      <h3 className="u-margin-A-n">
                        <strong>{props.profile.name}</strong>
                      </h3>
                    </div>

                    <div className={cx(style.attributesSticky, 'd-flex flex-1 justify-content-end')}>
                      {
                        (props.profile.programAttributes || []).map((attribute, idx) => (
                          <div key={idx} className="align-items-center d-flex" style={{ gap: 10 }}>
                            <FontAwesomeIcon icon={attribute.icon} />

                            <div>{attribute.value}</div>
                          </div>
                        ))
                      }
                    </div>
                  </>
                )
              }


            </div>
          </div>
        </div>
      </div>

      <div
        className="hidden-sm hidden-xs"
        style={{
          backgroundColor: '#fff',
          position: 'sticky',
          top: props.offsetY + props.stickyHeroBarHeight,
          // Below style.categoryProfileListOverlay which has $zindex-navbar-fixed
          zIndex: 1000
        }}
      >
        <div className="container">
          {
            isLoaded && (
              <SectionNav
                height={props.sectionNavHeight}
                offsetY={props.offsetY + props.stickyHeroBarHeight}
                sections={props.profile.contents || []}
              />
            )
          }
        </div>
      </div>
    </>
  );
};

export default ProfileHeroAndSectionNav;
